import Avo from 'analytics/Avo';
import type { ServiceLine } from 'api/types/service.types';
import Title from 'components/atoms/Title';
import { useConfig } from 'context/app-config';
import CreditsBadge from 'packages/shared/newOrder/components/CreditsBadge';
import { selectSelectedServices } from 'pages/newOrder/newOrder.selectors';
import { toggleSelectedServiceLines } from 'pages/newOrder/newOrder.slice';
import React, { useCallback, useMemo } from 'react';
import { Col, ListGroup, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import useCartEstimator from 'store/cart-estimator';
import styled from 'styled-components';

import ServiceItem from './service-item';

const StyledDiv = styled.div`
  margin-top: ${({ theme }) => theme.components.selectService.marginTop ?? '40'}px;
`;

interface Props {
  togglePricingSheet: (show: boolean, serviceLine: ServiceLine) => void;
  isShowCredits?: boolean;
  hideHeading?: boolean;
}

const SelectService = ({ togglePricingSheet, hideHeading = false, isShowCredits }: Props) => {
  const clearEstimator = useCartEstimator.use.clear();
  const { t } = useTranslation();
  const selectedServiceLines = useSelector(selectSelectedServices);
  const dispatch = useDispatch();
  const config = useConfig();

  const Heading = useMemo(
    () => (
      // if credits are shown, add margin top to the title
      <Col className={isShowCredits ? 'mt-2' : ''}>
        <Title>{t('common.selectServiceStepTitle')}</Title>
      </Col>
    ),
    [t, isShowCredits]
  );

  const services = useMemo(() => config?.services ?? [], [t, config?.services]);

  const onPressPricing = useCallback(
    (serviceLine: ServiceLine) => {
      Avo.viewPricing({ screen: 'Select Service Section' });
      togglePricingSheet?.(true, serviceLine);
    },
    [togglePricingSheet]
  );

  const onServiceClick = useCallback(
    (service: ServiceLine) => () => {
      if (!selectedServiceLines.includes(service)) {
        Avo.selectServiceLine({
          service,
        });
      }

      dispatch(toggleSelectedServiceLines(service));

      // clearing estimator in case of service change
      clearEstimator();
    },
    [clearEstimator, dispatch]
  );

  const Header = useMemo(
    () => (
      <Row className="justify-content-center align-items-center" noGutters>
        {Heading}
        {isShowCredits && <CreditsBadge mode="column" />}
      </Row>
    ),
    [isShowCredits, Heading]
  );

  const ServicesItems = useMemo(
    () =>
      services.map((service) => (
        <ServiceItem key={service.service} {...service} onCick={onServiceClick(service.service)} onPressPricing={onPressPricing} />
      )),
    [services, onServiceClick, onPressPricing]
  );

  return (
    <StyledDiv>
      {!hideHeading && Header}
      <Row>
        <ListGroup variant="flush" className="w-100">
          {ServicesItems}
        </ListGroup>
      </Row>
    </StyledDiv>
  );
};

export default SelectService;
